// ============================================================
// AUTH CONTEXT
// Provides authenticated user state, custom claims (role),
// and auth operations throughout the app.
// ============================================================

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
  type ReactNode,
} from 'react';
import {
  onIdTokenChanged,
  signOut,
  type User,
} from 'firebase/auth';
import { doc, onSnapshot, collection, query, where, getDocs, setDoc, serverTimestamp } from 'firebase/firestore';
import { firebaseAuth, firestore } from '../firebase/config';
import type { UserDoc, UserRole } from '@jobnvisa/shared';

interface AuthClaims {
  role: UserRole;
  agencyId?: string;
  linkedEntityId?: string;
}

interface AuthContextValue {
  firebaseUser: User | null | undefined;
  userDoc: UserDoc | null;
  claims: AuthClaims | null;
  role: UserRole | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  refreshToken: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [firebaseUser, setFirebaseUser] = useState<User | null | undefined>(undefined);
  const [userDoc, setUserDoc] = useState<UserDoc | null>(null);
  const [claims, setClaims] = useState<AuthClaims | null>(null);
  const claimsRef = useRef<AuthClaims | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Update ref synchronously before scheduling state update — prevents race condition
  // where a Firestore snapshot fires between setClaims() and the next render,
  // seeing stale claimsRef.current and triggering another refreshWithRetry loop.
  const setClaimsSync = useCallback((newClaims: AuthClaims | null) => {
    claimsRef.current = newClaims;
    setClaims(newClaims);
  }, []);

  const refreshToken = useCallback(async () => {
    if (!firebaseUser) return;
    const idTokenResult = await firebaseUser.getIdTokenResult(true);
    setClaimsSync({
      role: idTokenResult.claims.role as UserRole,
      agencyId: idTokenResult.claims.agencyId as string | undefined,
      linkedEntityId: idTokenResult.claims.linkedEntityId as string | undefined,
    });
  }, [firebaseUser, setClaimsSync]);

  const logout = useCallback(async () => {
    await signOut(firebaseAuth);
  }, []);

  // Auth state listener
  useEffect(() => {
    const unsubAuth = onIdTokenChanged(firebaseAuth, async (user) => {
      setFirebaseUser(user);

      if (!user) {
        setUserDoc(null);
        setClaimsSync(null);
        setIsLoading(false);
        return;
      }

      // Extract custom claims from JWT
      try {
        const idTokenResult = await user.getIdTokenResult();
        if (idTokenResult.claims.role) {
          setClaimsSync({
            role: idTokenResult.claims.role as UserRole,
            agencyId: idTokenResult.claims.agencyId as string | undefined,
            linkedEntityId: idTokenResult.claims.linkedEntityId as string | undefined,
          });
        }
      } catch (err: any) {
        console.log('Error getting ID token claims:', err);
        const code = err.code || '';
        if (
          code.includes('expired') || 
          code.includes('user-not-found') || 
          code.includes('user-disabled') || 
          code.includes('invalid-user-token') ||
          code.includes('user-token-expired')
        ) {
          console.warn('[useAuth] Token expired or invalid. Logging out automatically.');
          signOut(firebaseAuth);
        }
      }

      setIsLoading(false);
    });

    return unsubAuth;
  }, []);

  // Firestore user document listener (real-time)
  useEffect(() => {
    if (!firebaseUser) {
      setUserDoc(null);
      return;
    }

    const userRef = doc(firestore, 'users', firebaseUser.uid);
    const unsubDoc = onSnapshot(userRef, async (snap) => {
      if (snap.exists()) {
        const udata = snap.data() as UserDoc;
        setUserDoc(udata);

        // Auto-refresh token if Firestore role exists but claims don't match yet
        if (udata.role && (!claimsRef.current || claimsRef.current.role !== udata.role)) {
          const refreshWithRetry = async (attempt = 1) => {
            try {
              const idTokenResult = await firebaseUser.getIdTokenResult(true);
              if (idTokenResult.claims.role === udata.role) {
                setClaimsSync({
                  role: idTokenResult.claims.role as UserRole,
                  agencyId: idTokenResult.claims.agencyId as string | undefined,
                  linkedEntityId: idTokenResult.claims.linkedEntityId as string | undefined,
                });
                console.log('[useAuth] ID token claims updated successfully for role:', udata.role);
              } else if (attempt < 4) {
                console.log(`[useAuth] ID token claims not set yet. Retrying refresh in 1.5s (attempt ${attempt}/3)...`);
                setTimeout(() => refreshWithRetry(attempt + 1), 1500);
              }
            } catch (tokenErr) {
              console.warn('[useAuth] Error refreshing ID token:', tokenErr);
            }
          };
          refreshWithRetry();
        }
      } else {
        // Fallback check: query by phoneNumber or email if UID snapshot was empty
        let foundDoc: UserDoc | null = null;
        try {
          if (firebaseUser.phoneNumber) {
            const q = query(collection(firestore, 'users'), where('phoneNumber', '==', firebaseUser.phoneNumber));
            const querySnap = await getDocs(q);
            if (!querySnap.empty) {
              foundDoc = querySnap.docs[0].data() as UserDoc;
            }
          } else if (firebaseUser.email) {
            const q = query(collection(firestore, 'users'), where('email', '==', firebaseUser.email));
            const querySnap = await getDocs(q);
            if (!querySnap.empty) {
              foundDoc = querySnap.docs[0].data() as UserDoc;
            }
          }
        } catch (e) {
          console.log('Fallback query error:', e);
        }

         if (foundDoc) {
          setUserDoc(foundDoc);
        } else {
          // Construct fallback UserDoc locally to prevent getting stuck on "Loading your profile..."
          // but DO NOT write it back to Firestore (setDoc) to avoid overwriting existing documents due to transient loading/network glitches.
          const storedRole = sessionStorage.getItem('jobnvisa_registered_role') as UserRole | null;
          const derivedRole: UserRole = 
            storedRole ||
            (firebaseUser.email?.includes('agency') ? 'agency' :
             firebaseUser.email?.includes('employer') ? 'employer' :
             firebaseUser.email?.includes('referrer') ? 'referrer' :
             (firebaseUser.email?.includes('superadmin') || firebaseUser.email?.includes('admin')) ? 'superadmin' : 'candidate');

          const fallbackDoc: UserDoc = {
            uid: firebaseUser.uid,
            email: firebaseUser.email || 'user@jobnvisa.com',
            role: derivedRole,
            displayName: firebaseUser.displayName || firebaseUser.email?.split('@')[0] || 'User',
            phoneNumber: firebaseUser.phoneNumber || '+91 9876543210',
            status: derivedRole === 'candidate' ? 'active' : 'pending_verification',
            emailVerified: true,
            phoneVerified: true,
            createdAt: new Date().toISOString() as any,
            updatedAt: new Date().toISOString() as any,
          };

          setUserDoc(fallbackDoc);
        }
      }
    });

    return unsubDoc;
  }, [firebaseUser]);

  const effectiveRole = claims?.role ?? userDoc?.role ?? null;

  const value: AuthContextValue = {
    firebaseUser,
    userDoc,
    claims,
    role: effectiveRole,
    isLoading,
    isAuthenticated: !!firebaseUser && !!(claims || userDoc) && userDoc?.status === 'active',
    refreshToken,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
